/**
 * Result type for error handling
 * Represents either a success (Ok) or failure (Err) state
 */
export type Result<T, E = Error> = Ok<T> | Err<E>

export interface Ok<T> {
  readonly kind: 'ok'
  readonly value: T
}

export interface Err<E> {
  readonly kind: 'error'
  readonly error: E
}

export const createOk = <T>(value: T): Ok<T> => ({
  kind: 'ok',
  value,
})

export const createErr = <E>(error: E): Err<E> => ({
  kind: 'error',
  error,
})

export const isOk = <T, E>(result: Result<T, E>): result is Ok<T> =>
  result.kind === 'ok'

export const isErr = <T, E>(result: Result<T, E>): result is Err<E> =>
  result.kind === 'error'

export const match = <T, E, U>(
  result: Result<T, E>,
  onOk: (value: T) => U,
  onErr: (error: E) => U
): U => {
  return isOk(result) ? onOk(result.value) : onErr(result.error)
}

export const unwrap = <T, E>(result: Result<T, E>): T => {
  if (isOk(result)) {
    return result.value
  }
  throw result.error
}

export const unwrapOr = <T, E>(result: Result<T, E>, defaultValue: T): T => {
  return isOk(result) ? result.value : defaultValue
}

export const map = <T, E, U>(
  result: Result<T, E>,
  fn: (value: T) => U
): Result<U, E> => {
  return isOk(result) ? createOk(fn(result.value)) : result
}

export const mapErr = <T, E, F>(
  result: Result<T, E>,
  fn: (error: E) => F
): Result<T, F> => {
  return isErr(result) ? createErr(fn(result.error)) : result
}

export const flatMap = <T, E, U>(
  result: Result<T, E>,
  fn: (value: T) => Result<U, E>
): Result<U, E> => {
  return isOk(result) ? fn(result.value) : result
}